import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ethers } from 'ethers'; // Import ethers
import { useChainId } from 'wagmi';
import useDebounce from '../../hooks/useDebounce';
import useIsWindowVisible from '../../hooks/useIsWindowVisible';
import { updateBlockNumber } from './actions';
import { resetProvider } from '../../utils/provider';

export default function Updater(): null {
  const [provider, setProvider] = useState<ethers.providers.Web3Provider | null>(null);
  const [providerErrorCount, setProviderErrorCount] = useState<number>(0);
  const chainId = useChainId();

  const dispatch = useDispatch();
  const windowVisible = useIsWindowVisible();

  const [state, setState] = useState<{
    chainId: number | undefined;
    blockNumber: number | null;
  }>({
    chainId,
    blockNumber: null,
  });

  const blockNumberCallback = useCallback(
    (blockNumber: number) => {
      setState((state) => {
        if (chainId === state.chainId) {
          if (typeof state.blockNumber !== 'number') return { chainId, blockNumber };
          return { chainId, blockNumber: Math.max(blockNumber, state.blockNumber) };
        }
        return state;
      });
    },
    [chainId, setState],
  );

  // Set up the provider manually
  useEffect(() => {
    if (typeof window !== 'undefined' && window.ethereum) {
      const newProvider = new ethers.providers.Web3Provider(window.ethereum);
      setProvider(newProvider);
    }
  }, []);

  // attach/detach listeners
  useEffect(() => {
    if (!provider || !chainId || !windowVisible) return undefined;
    setState({ chainId, blockNumber: null });

    provider
      .getBlockNumber()
      .then(blockNumberCallback)
      .catch((error) => {
        console.error(`Failed to get block number for chainId: ${chainId}`, error);
        console.log(`Provider error count: ${providerErrorCount + 1}`);
        try { resetProvider(); } catch (_) { }
        setProviderErrorCount(providerErrorCount + 1);
      });

    const onBlock = (blockNumber: number) => {
      blockNumberCallback(blockNumber);
    };

    provider.on('block', onBlock);

    return () => {
      provider.off('block', onBlock);
    };
  }, [dispatch, chainId, provider, blockNumberCallback, windowVisible, providerErrorCount]);

  const debouncedState = useDebounce(state, 100);

  useEffect(() => {
    if (!debouncedState.chainId || !debouncedState.blockNumber || !windowVisible) return;
    dispatch(
      updateBlockNumber({
        chainId: debouncedState.chainId,
        blockNumber: debouncedState.blockNumber,
      }),
    );
  }, [windowVisible, dispatch, debouncedState.blockNumber, debouncedState.chainId]);

  return null;
}
